<template>
  <section class="bg-gray-50 py-16">
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <!-- Page Title -->
      <div class="text-center">
        <h2 class="text-xl font-bold uppercase tracking-wide text-primaryBG-600">
          {{ t('specialty_listing.title') }}
        </h2>
        <p class="mt-1 text-3xl font-extrabold text-gray-900 sm:text-4xl">
          {{ t('specialty_listing.subtitle') }}
        </p>
        <p class="mx-auto mt-4 max-w-2xl text-lg text-gray-500">
          {{ t('specialty_listing.description') }}
        </p>
      </div>

      <!-- Cards Container -->
      <div
        class="mx-auto mt-12 grid max-w-7xl grid-cols-1 gap-8
               sm:grid-cols-2 lg:grid-cols-3"
      >
        <!-- Card for each specialty -->
        <div
          v-for="(specialty, index) in specialty_data"
          :key="index"
          class="flex flex-col rounded-lg bg-white p-6 shadow transition-shadow duration-200 hover:shadow-md"
        >
          <!-- Small English name - only show for Chinese locales -->
          <p v-if="!isEnglishLocale" class="mt-1 text-sm font-medium text-primaryBG-600">
              {{ specialty.en_name }}
          </p>
          <!-- Specialty Title -->
          <div>
            <h3 :class="[
              'text-lg font-semibold',
              isEnglishLocale ? 'text-primaryBG-600' : 'text-gray-800'
            ]">
              {{ displayName(specialty) }}
              <div class="text-sm text-gray-500">
                {{ t('specialty_listing.alternate_names') }}
                <span class="ml-1" v-for="(alt, i) in specialty.cn_alts" :key="i">
                  {{ displayAltName(alt) }}
                </span>
              </div>
            </h3>
            
          </div>

          <!-- Common Illnesses -->
          <div class="mt-4">
            <h4 class="text-sm font-semibold text-gray-700">{{ t('specialty_listing.common_illnesses') }}</h4>
            <ul class="mt-2 list-inside list-disc space-y-1 text-sm text-gray-600">
              <li v-for="(illness, i) in specialty.common_illnesses" :key="i">
                <!-- Some Entries have English after Chinese, We will leave those in as a mean of A/B Testing -->
                {{ displayIllness(illness) }}
              </li>
            </ul>
          </div>

          <!-- CTA Button (optional) -->
          <div class="mt-auto pt-6">
            <NuxtLink
              :to="localePath('/specialty/' + specialty.en_name)"
              class="inline-flex items-center justify-center rounded-md bg-primaryBG-600 px-4 py-2 text-sm font-medium text-white shadow-sm
                     hover:bg-primaryBG-700 focus:outline-none focus:ring-2 focus:ring-primaryBG-500 focus:ring-offset-2"
            >
              {{ t('specialty_listing.learn_more') }} {{ displayName(specialty) }}
            </NuxtLink>
            
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useLocalePath } from '#i18n'
import { tify } from 'chinese-conv'
import { computed } from 'vue'

const props = defineProps({
  specialty_data: {
    type: Array,
    required: true,
  },
});

// i18n & routing helpers
const { locale, t } = useI18n()
const localePath = useLocalePath()

// Computed property to check if current locale is English
const isEnglishLocale = computed(() => locale.value === 'en-US')

// Display name based on locale
function displayName(specialty) {
  if (locale.value === 'en-US') return specialty.en_name
  if (locale.value === 'zh-Hant') return tify(specialty.cn_name)
  return specialty.cn_name
}

// Display alternate name based on locale
function displayAltName(altName) {
  if (locale.value === 'zh-Hant') return tify(altName)
  return altName
}

// Display illness based on locale
function displayIllness(illness) {
  if (locale.value === 'zh-Hant') return tify(illness)
  if (locale.value === 'en-US') {
    const key = `illness.${illness}`
    const translated = t(key)
    return translated !== key ? translated : illness
  }
  return illness
}
</script>
